/**
 * In-game settings menu component.
 * Overlay that appears over the paused game, allowing quick settings adjustments.
 * Renders immediately without delay and shows the paused game in the background.
 */

import { useState, useEffect, useRef } from 'react';
import { useSettingsStore } from '@shared/stores/settingsStore';
import { useDevSettingsStore, isDevSettingsAvailable } from '@shared/stores/devSettingsStore';
import { useDevRuntimeStore } from '@shared/stores/devRuntimeStore';
import { useGameStateStore } from '@shared/stores';
import { getSettingsByCategory, ConfigCategory, ConfigVisibility } from '@shared/config/utils';
import { CAMERA_CONFIG_SCHEMA } from '@shared/config/settings/camera';
import RegenerateMapButton from './RegenerateMapButton';

interface InGameSettingsMenuProps {
  scene?: unknown;
}

export default function InGameSettingsMenu({ scene }: InGameSettingsMenuProps = {}) {
  const { settings, updateCameraSettings, resetCameraSettings } = useSettingsStore();
  const { settings: devSettings } = useDevSettingsStore();
  const { hideInGameSettingsMenu, settingsContext } = useGameStateStore();
  const { useTileImagesOverride, setUseTileImagesOverride } = useDevRuntimeStore();
  type SettingsTab = ConfigCategory | 'DEV';
  const [activeCategory, setActiveCategory] = useState<SettingsTab>(
    settingsContext === 'dev' && isDevSettingsAvailable() ? 'DEV' : ConfigCategory.CAMERA
  );
  const previousSettingsRef = useRef<string>('');

  // Refresh camera config when settings change (with error handling)
  useEffect(() => {
    if (!scene) return;

    const sceneWithMethods = scene as {
      getCameraController?: () => {
        refreshConfig?: () => void;
      } | null;
    };

    const cameraController = sceneWithMethods.getCameraController?.();
    if (cameraController?.refreshConfig) {
      // Create a string representation of current settings to detect changes
      const currentSettings = JSON.stringify({
        player: settings.camera,
        dev: devSettings.camera,
      });

      // Only refresh if settings actually changed
      if (currentSettings !== previousSettingsRef.current) {
        try {
          cameraController.refreshConfig();
          previousSettingsRef.current = currentSettings;
        } catch (error) {
          // Silently handle errors - camera might not be ready yet
          console.warn('Failed to refresh camera config:', error);
        }
      }
    }
  }, [settings.camera, devSettings.camera, scene]);

  // Get player-visible settings grouped by category
  const settingsByCategory = getSettingsByCategory(ConfigVisibility.PLAYER);

  const handleCameraSettingChange = (key: string, value: number) => {
    // Get bounds from dev settings (with safety check)
    const bounds = devSettings?.cameraBounds?.[key as keyof typeof devSettings.cameraBounds];
    if (bounds) {
      // Clamp value to bounds
      const clampedValue = Math.max(bounds.min, Math.min(bounds.max, value));
      updateCameraSettings({ [key]: clampedValue });
    } else {
      updateCameraSettings({ [key]: value });
    }
  };

  const categories = Object.keys(settingsByCategory) as ConfigCategory[];
  const tabs: SettingsTab[] = isDevSettingsAvailable() ? [...categories, 'DEV'] : categories;

  return (
    <div className="absolute inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-gray-800 rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white">Settings</h2>
            <button
              onClick={hideInGameSettingsMenu}
              className="px-4 py-2 bg-gray-700 text-gray-200 rounded hover:bg-gray-600 transition-colors"
            >
              Close
            </button>
          </div>

          {/* Category tabs */}
          <div className="flex gap-2 mb-6">
            {tabs.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-4 py-2 rounded transition-colors ${
                  activeCategory === category
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                }`}
              >
                {category === 'DEV' ? 'Dev' : category.charAt(0).toUpperCase() + category.slice(1)}
              </button>
            ))}
          </div>

          {/* Settings content */}
          <div className="space-y-6">
            {activeCategory === ConfigCategory.CAMERA && (
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Camera Settings</h3>
                {Object.entries(CAMERA_CONFIG_SCHEMA)
                  .filter(([_, metadata]) => metadata.visibility === ConfigVisibility.PLAYER)
                  .map(([key, metadata]) => (
                    <div key={key} className="space-y-2 mb-4">
                      <label className="block text-gray-200">
                        <span className="font-medium">{metadata.label}</span>
                        {metadata.description && (
                          <span className="block text-sm text-gray-400 mt-1">
                            {metadata.description}
                          </span>
                        )}
                      </label>
                      <div className="flex items-center gap-4">
                        {(() => {
                          // Get bounds from dev settings, fallback to schema defaults
                          const bounds =
                            devSettings?.cameraBounds?.[
                              key as keyof typeof devSettings.cameraBounds
                            ];
                          const min = bounds?.min ?? metadata.min ?? 0;
                          const max = bounds?.max ?? metadata.max ?? 100;
                          const step = metadata.step ?? 0.1;
                          const currentValue = settings.camera[
                            key as keyof typeof settings.camera
                          ] as number;
                          return (
                            <>
                              <input
                                type="range"
                                min={min}
                                max={max}
                                step={step}
                                value={currentValue}
                                onChange={(e) =>
                                  handleCameraSettingChange(key, parseFloat(e.target.value))
                                }
                                className="flex-1"
                              />
                              <span className="text-white font-mono w-16 text-right">
                                {currentValue}
                              </span>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  ))}
                <div className="pt-4 border-t border-gray-700">
                  <button
                    onClick={resetCameraSettings}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                  >
                    Reset Camera Settings
                  </button>
                </div>
              </div>
            )}

            {/* Future: Add other categories (Audio, Graphics, Controls, etc.) */}

            {activeCategory === 'DEV' && isDevSettingsAvailable() && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-white">Developer Tools</h3>

                {/* Tile images toggle */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-medium">Use Tile Images</p>
                      <p className="text-sm text-gray-400">
                        Overrides VITE_USE_TILE_IMAGES for this session (dev only).
                      </p>
                    </div>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={useTileImagesOverride ?? true}
                        onChange={async (e) => {
                          setUseTileImagesOverride(e.target.checked);
                          const sceneWithRefresh = scene as
                            | {
                                refreshTerrainVisuals?: () => Promise<void>;
                              }
                            | undefined;
                          await sceneWithRefresh?.refreshTerrainVisuals?.();
                        }}
                        className="h-4 w-4"
                      />
                      <span className="text-gray-200 text-sm">
                        {useTileImagesOverride === false ? 'Disabled' : 'Enabled'}
                      </span>
                    </label>
                  </div>
                  <button
                    onClick={async () => {
                      setUseTileImagesOverride(null);
                      const sceneWithRefresh = scene as
                        | {
                            refreshTerrainVisuals?: () => Promise<void>;
                          }
                        | undefined;
                      await sceneWithRefresh?.refreshTerrainVisuals?.();
                    }}
                    className="px-3 py-2 bg-gray-700 text-gray-200 rounded hover:bg-gray-600 transition-colors text-sm"
                  >
                    Reset to default (env)
                  </button>
                </div>

                {/* Map regeneration */}
                <div className="space-y-2">
                  <p className="text-white font-medium">Regenerate Procedural Map</p>
                  <p className="text-sm text-gray-400">
                    Generates a fresh procedural map using a new seed. Dev only.
                  </p>
                  <RegenerateMapButton scene={scene} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
